import type { ReactNode } from 'react'
import { Moon, Sun } from 'lucide-react'
import { Link } from '../lib/router.tsx'
import { Button } from './ui/button.tsx'
import { cn } from '../lib/utils.ts'
import { useAdminMode } from '../lib/useAdminMode.tsx'
import type { Theme } from '../lib/theme.ts'

export function Shell({
  title, subtitle, actions, children, theme, onToggleTheme,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  theme: Theme
  onToggleTheme: () => void
}) {
  const adminMode = useAdminMode()
  return (
    <div className="min-h-dvh">
      <a href="#obsah" className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-[var(--radius-md)] focus:bg-[var(--card)] focus:px-3 focus:py-2">
        Přeskočit na obsah
      </a>

      <header className="border-b border-[var(--border)] bg-[var(--card)] no-print">
        {/* Na mobilu jsou to tři pásy pod sebou (značka + motiv, akce, správa),
            od `sm` jedna lajna. Pořadí řídí `order`, ne pořadí v DOM, aby
            odečítač četl značku → navigaci → akce. */}
        <div className="mx-auto flex max-w-[92rem] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 sm:px-[var(--space-6)]">
          <Link to="/" className="order-1 font-serif text-[length:var(--text-lg)] font-semibold tracking-[var(--tracking-title)]">
            Seznam četby
          </Link>

          {/* Student tyhle obrazovky nepotřebuje; objeví se až se správou. */}
          {adminMode.on && (
            <nav className="order-4 -mx-1 flex w-full items-center gap-1 overflow-x-auto px-1 text-[length:var(--text-sm)] sm:order-2 sm:mx-0 sm:w-auto sm:overflow-visible sm:px-0">
              <Link to="/doporucena" className="shrink-0 rounded-[var(--radius-md)] px-2.5 py-1.5 hover:bg-[var(--muted)]">Doporučená</Link>
              <Link to="/kanon" className="shrink-0 rounded-[var(--radius-md)] px-2.5 py-1.5 hover:bg-[var(--muted)]">Kánon</Link>
              <Link to="/settings" className="shrink-0 rounded-[var(--radius-md)] px-2.5 py-1.5 hover:bg-[var(--muted)]">Kritéria</Link>
              <Link to="/import" className="shrink-0 rounded-[var(--radius-md)] px-2.5 py-1.5 hover:bg-[var(--muted)]">Import</Link>
              <span className="ml-1 shrink-0 rounded-[var(--radius-sm)] bg-[var(--rule-excess-surface)] px-1.5 py-0.5 text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-label)] text-[var(--rule-excess)]">
                správa
              </span>
            </nav>
          )}

          {actions && (
            <div className="order-3 flex w-full items-center gap-2 *:flex-1 sm:ml-auto sm:w-auto sm:*:flex-none">
              {actions}
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}
            className={cn('order-2 ml-auto shrink-0 sm:order-4', actions && 'sm:ml-0')}
          >
            {theme === 'dark' ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
          </Button>
        </div>
      </header>

      <main id="obsah-main" className="mx-auto max-w-[92rem] px-4 py-5 sm:px-[var(--space-6)] sm:py-[var(--space-8)]">
        <div id="obsah" className="mb-[var(--space-5)] sm:mb-[var(--space-6)]">
          <h1 className="text-[length:var(--text-xl)] font-bold tracking-[var(--tracking-tight)] sm:text-[length:var(--text-2xl)]">{title}</h1>
          {subtitle && <p className="mt-1 text-[length:var(--text-sm)] text-[var(--muted-foreground)]">{subtitle}</p>}
        </div>
        {children}
      </main>
    </div>
  )
}
